import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Landmark, LineChart, ShieldCheck, HeartHandshake, ScrollText } from "lucide-react";

const pillars = [
  {
    icon: Landmark,
    title: "Family Banking System",
    visible: "Build a foundation for liquidity, financing, repayment, and the intentional reuse of capital.",
    expanded: [
      "A Family Banking System helps organize how money moves through the household. Instead of relying entirely on outside lenders or draining savings without a plan to rebuild them, the family creates a system for storing capital, accessing liquidity, financing purchases, repaying loans, and reusing money over time.",
      "A properly structured whole life policy may support this process, but the policy is not the entire strategy. The larger goal is to create more control over cash flow and capital.",
    ],
  },
  {
    icon: LineChart,
    title: "Market Exposure",
    visible: "Position appropriate capital for long-term growth opportunities while respecting risk and liquidity needs.",
    expanded: [
      "Market exposure can play an important role in building wealth, but it should not be expected to perform every financial job. Money needed for near-term access, protection, or dependable income may require a different role than money positioned for long-term growth.",
      "The purpose is not to prescribe one investment approach. It is to help families define which dollars can accept market risk and how those decisions fit into the complete system.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Guaranteed Income for Life",
    visible: "Create dependable lifetime income for the portion of retirement that should not depend entirely on market performance.",
    expanded: [
      "Accumulating assets and distributing income are different challenges. This pillar helps families examine whether part of their retirement plan should create contractual income they cannot outlive.",
      "Income tools must be evaluated according to their terms, purpose, liquidity, and the claims-paying ability of the issuing insurer. They should support the broader system rather than replace thoughtful planning.",
    ],
  },
  {
    icon: HeartHandshake,
    title: "Long-Term Care Protection",
    visible: "Protect income, assets, family choices, and the legacy from the financial effects of extended care.",
    expanded: [
      "Long-term care is not only a healthcare concern. It can affect family cash flow, caregivers, retirement income, assets, and the legacy intended for future generations.",
      "Planning creates options before a difficult season occurs. The goal is not fear-based selling. The goal is helping the family understand how care would be coordinated and funded.",
    ],
  },
  {
    icon: ScrollText,
    title: "Trust, Legacy, and Tax Strategy",
    visible: "Coordinate how assets, values, instructions, and opportunities are protected and transferred.",
    expanded: [
      "Legacy planning is bigger than leaving money behind. It includes trusts, beneficiary decisions, family education, written instructions, tax coordination, and the values that help the next generation become capable stewards.",
      "Legal and tax strategies must be completed with qualified professionals. Legacy Life & Retirement helps families understand how these conversations connect to the larger financial system.",
    ],
  },
];

export const FivePillarsSection = () => {
  const [open, setOpen] = useState(-1);
  // Desktop (lg): 6-col grid — two centered cards on top, three centered beneath.
  const desktopPos = [
    "lg:col-span-2 lg:col-start-2 lg:row-start-1",
    "lg:col-span-2 lg:col-start-4 lg:row-start-1",
    "lg:col-span-2 lg:col-start-1 lg:row-start-2",
    "lg:col-span-2 lg:col-start-3 lg:row-start-2",
    "lg:col-span-2 lg:col-start-5 lg:row-start-2",
  ];

  return (
    <section id="framework" data-testid="five-pillars-section" className="section-lr bg-[#F2EBDC]">
      <div className="container-lr">
        <div className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="gold-rule" />
            <p className="eyebrow">The Legacy Financial System</p>
          </div>
          <h2 className="section-heading mt-6 text-legacy"><span className="accent-word">Five Pillars.</span> One Complete Financial System.</h2>
          <p className="mt-5 text-lg leading-relaxed text-legacy/85">Five interconnected pillars designed to help your family build, protect, and enjoy wealth with greater confidence.</p>
          <p className="mt-4 text-slate leading-relaxed">Most financial plans are built one product at a time. The Legacy Financial System takes a different approach by helping each part of your financial life work together.</p>
          <p className="mt-6 text-sm font-semibold tracking-wide text-bronze">Click each pillar to learn more.</p>
        </div>

        <div className="mt-12 grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {pillars.map(({ icon: Icon, title, visible, expanded }, index) => {
            const isOpen = open === index;
            const contentId = `pillar-panel-${index}`;
            return (
              <motion.article key={title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: index * 0.05 }} className={`group lr-card self-start overflow-hidden transition-colors duration-300 hover:bg-legacy hover:border-legacy ${desktopPos[index]}`}>
                <button type="button" aria-expanded={isOpen} aria-controls={contentId} onClick={() => setOpen(isOpen ? -1 : index)} className="focus-ring flex min-h-[17.5rem] w-full flex-col p-6 text-left">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gold/40 bg-ivory text-bronze transition-colors duration-300 group-hover:bg-gold group-hover:text-legacy">
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <span className="mt-4 flex items-start justify-between gap-3">
                    <span className="font-serif text-xl leading-tight text-legacy transition-colors duration-300 group-hover:text-ivory">{title}</span>
                    <ChevronDown className={`mt-1 shrink-0 text-bronze transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} size={18} />
                  </span>
                  <span className="mt-3 block text-sm leading-relaxed text-slate transition-colors duration-300 line-clamp-3 group-hover:text-taupe">{visible}</span>
                  <span className="mt-auto pt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-bronze transition-colors duration-300 group-hover:text-gold">{isOpen ? "Show less" : "Learn more"}</span>
                </button>
                <div id={contentId} className={`accordion-panel ${isOpen ? "is-open" : ""}`}>
                  <div>
                    <div className="px-6 pb-6">
                      <div className="border-t border-gold/25 pt-4 text-sm leading-relaxed text-slate transition-colors duration-300 group-hover:text-taupe">
                        {expanded.map((p) => <p key={p} className="mb-3 last:mb-0">{p}</p>)}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
